'use client';

import React, { useEffect, useImperativeHandle, forwardRef, useState } from 'react';
import {
  NumericFacet as HeadlessNumericFacet,
  NumericFacetState,
} from '@coveo/headless-react/ssr-commerce';
import { FilterGroup, RangeFilterType } from '@greystar/designsystem';
import { OptionProps } from '../../../component-props';
import { formatCurrency } from '@greystar/lib';

interface NumericRange {
  start: number;
  end: number;
}

interface INumericFacetProps {
  controller?: HeadlessNumericFacet;
  staticState: NumericFacetState;
  range: NumericRange;
  onRangeChange: (range: NumericRange, changed: 'start' | 'end') => void;
  analytics?: {
    trackEvent: <T extends Record<string, unknown>>(event: string, data: T) => void;
  };
}

export interface ClearFacetRef {
  onClickClearSelectedFacetValues: () => void;
  isLoading: boolean;
}

const NumericFacet = forwardRef<ClearFacetRef, INumericFacetProps>((props, ref) => {
  const { controller, staticState, range, onRangeChange, analytics } = props;
  const [state, setState] = useState(staticState);
  const [currentManualRange, setCurrentManualRange] = useState(range);

  useEffect(() => {
    setCurrentManualRange(range);
  }, [range]);

  useEffect(() => {
    if (!controller) return;
    const unsubscribe = controller.subscribe(() => setState(controller.state));
    return () => unsubscribe();
  }, [controller]);

  const onClickClearSelectedFacetValues = (): void => {
    controller?.deselectAll();
    const clearedRange = {
      start: state.domain?.min ?? staticState.values[0]?.start ?? 0,
      end: state.domain?.max ?? staticState.values[1]?.end ?? 0,
    };
    setCurrentManualRange(clearedRange);
    onRangeChange(clearedRange, 'start');
    analytics?.trackEvent('filters_clear', {
      action_type: 'budget filters - clear',
    });
  };

  useImperativeHandle(ref, () => ({
    onClickClearSelectedFacetValues,
    isLoading: state.isLoading,
  }));

  const minValue = state.values[0]?.start ?? 0;
  const maxValue = state.values[state.values.length - 1]?.end ?? 0;

  const generateRangeOptions = (
    min: number,
    max: number,
    currencySymbol = '$',
    fractionDigits?: number
  ): OptionProps[] => {
    const options: OptionProps[] = [];
    const rangeDiff = max - min;
    const step = rangeDiff < 100 ? 10 : rangeDiff < 500 ? 50 : 500; // Step 10 if < 100, 50 if < 500, else 500
    for (let value = min; value <= max; value += step) {
      if (value <= max) {
        // Ensure value doesn't exceed max
        options.push({
          label: formatCurrency(value, currencySymbol, fractionDigits),
          value: `${value}`,
        });
      }
    }
    return options;
  };

  const minOptions = generateRangeOptions(minValue, maxValue, '$');
  const getMaxOptions = () => {
    const min = currentManualRange.start || minValue;
    return generateRangeOptions(min, maxValue, '$');
  };

  const handleStartChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const start = parseInt(e.target.value);
    const maxOptions = getMaxOptions();
    const defaultMax = parseInt(maxOptions[maxOptions.length - 1]?.value ?? `${maxValue}`);
    const newRange = {
      start,
      end: defaultMax, // Set to last max option
    };
    setCurrentManualRange(newRange);
    analytics?.trackEvent('filters_selection', {
      action_type: 'budget min price',
      action_value: start.toString(),
    });
    onRangeChange(newRange, 'start');
  };

  const handleEndChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const end = parseInt(e.target.value);
    const newRange = {
      start: currentManualRange.start, // Use current start value without modification
      end,
    };
    setCurrentManualRange(newRange);
    analytics?.trackEvent('filters_selection', {
      action_type: 'budget max price',
      action_value: end.toString(),
    });
    onRangeChange(newRange, 'end');
  };

  return (
    <FilterGroup key={state.displayName ?? state.facetId}>
      <FilterGroup.Header title={state.displayName ?? state.facetId} />
      <FilterGroup.Body>
        <div className="ManualRangeControls">
          <RangeFilterType
            heading={`Total monthly leasing prices for the area you've searched.`}
            minSelect={{
              label: { header: 'Minimum Price' },
              options: minOptions,
              selectProps: {
                id: 'manualRangeStart',
                name: 'min-price',
                value: currentManualRange.start.toString(),
                onChange: handleStartChange,
              },
              defaultOption: { label: 'Select', value: 'Select' },
            }}
            maxSelect={{
              label: { header: 'Maximum Price' },
              options: getMaxOptions(),
              selectProps: {
                id: 'manualRangeEnd',
                name: 'max-price',
                value:
                  currentManualRange.end === (state.domain?.max ?? 0)
                    ? getMaxOptions().at(-1)?.value ?? ''
                    : currentManualRange.end.toString(),
                onChange: handleEndChange,
              },
              defaultOption: { label: 'Select', value: 'Select' },
            }}
          />
        </div>
      </FilterGroup.Body>
    </FilterGroup>
  );
});

NumericFacet.displayName = 'NumericFacet';
export default NumericFacet;
